import {
    checkItemTimeAvailabilityInMenu,
    getModOptionName,
} from 'components/Sell/utils/common'
import { groupBy, isEqual, keys, sum, uniqWith, values } from 'lodash-es'
import {
    ItemType,
    ModifierGroupContract,
    OrderLineLookupContract,
    POSVariantGroup,
    UnavailableItemsContract,
} from 'models'
import {
    ComboViewModel,
    EntityModifiersMap,
    ErrorOrderItemViewType,
    ItemViewModel,
    MenuViewModel,
    ModifierOptionLookup,
    OrderItemViewModel,
    PriceListData,
    UsedItem,
    UsedModifiers,
    UsedVariants,
} from 'store/models/sellModels'
import { getItemPrice, getModifierOptionPrice } from 'utilities/price/getPrice'

// Note: we cannot check quantity, priceAdd in orderpanel because the orderpanel was be transform price (get from price list) and quantity (get from current product)
interface IVariantOption {
    variantOptionId: string
    name: string
    priceAdd: number | undefined
}
interface IModifierOption {
    modifierOptionId: string
    bindingModfierGroupId: string
    name: string
    priceAdd: number | undefined
}
interface ICheckComboItem {
    itemId: string
    name: string
    quantity: number | undefined
    variantOptions: IVariantOption[]
    modifierOptions: IModifierOption[]
}

class OrderItemViewError {
    errors: ErrorOrderItemViewType
    strictTimeAvailability: boolean
    menus: MenuViewModel[]
    comboChoicesMap: Record<string, ComboViewModel[]>
    unavailableProducts: UnavailableItemsContract
    enableInventoryCheck: boolean
    modifierOptionsMap: ModifierOptionLookup
    priceListData: PriceListData
    variantGroupsMap: Record<string, POSVariantGroup>
    modifierGroupsMap: Record<string, ModifierGroupContract>
    itemsMap: Record<string, ItemViewModel>
    entityModifiersMap: EntityModifiersMap

    constructor(
        strictTimeAvailability: boolean,
        menus: MenuViewModel[],
        comboChoicesMap: Record<string, ComboViewModel[]>,
        unavailableProducts: UnavailableItemsContract,
        enableInventoryCheck: boolean,
        modifierOptionsMap: ModifierOptionLookup,
        priceListData: PriceListData,
        variantGroupsMap: Record<string, POSVariantGroup>,
        modifierGroupsMap: Record<string, ModifierGroupContract>,
        itemsMap: Record<string, ItemViewModel>,
        entityModifiersMap: EntityModifiersMap,
    ) {
        this.errors = {
            'item-unavailable': [],
            'price-changed': [],
            'time-unavailable': [],
            'invalid-modifier-option': [],
        }
        this.strictTimeAvailability = strictTimeAvailability
        this.menus = menus
        this.comboChoicesMap = comboChoicesMap
        this.unavailableProducts = unavailableProducts
        this.enableInventoryCheck = enableInventoryCheck
        this.modifierOptionsMap = modifierOptionsMap
        this.priceListData = priceListData
        this.variantGroupsMap = variantGroupsMap
        this.modifierGroupsMap = modifierGroupsMap
        this.itemsMap = itemsMap
        this.entityModifiersMap = entityModifiersMap
    }
    initial() {
        this.errors = {
            'item-unavailable': [],
            'price-changed': [],
            'time-unavailable': [],
            'invalid-modifier-option': [],
        }
    }

    /** validate item & variant and modfier and combo to see unavailable, item price/quantity has been changed, out of stock
     * @param orderLine order line from reloading order
     */
    validateOrderLine(orderLine: OrderLineLookupContract) {
        const item = orderLine.item

        // validate item & variant
        this.validateItem(
            orderLine.itemID!,
            orderLine?.item?.baseItemID ||
                orderLine.item?.baseDepositAndCreditItemID,
            item?.displayName || item?.name || 'Unknown',
            item?.categoryID || 'Unknown',
            orderLine.menuID!,
            orderLine.menuSectionID!,
            orderLine.price ?? 0,
            this.itemsMap[orderLine?.itemID ?? '']?.type ===
                ItemType.Miscellaneous,
        )

        // validate modifier
        orderLine.orderLineModifiers?.forEach((modifier) => {
            this.validateModifier(
                modifier.bindingModifierGroupID!,
                modifier.modifierOptionID!,
                modifier.modifierOption?.displayName || 'Unknown',
                modifier.priceAdd,
                orderLine.menuID!,
            )
        })

        // validate combo
        const comboChoiceGroup = groupBy(
            orderLine.subOrderLines,
            (i) => i.comboChoiceID,
        )
        keys(comboChoiceGroup).forEach((comboChoiceId) => {
            let comboItems = values(comboChoiceGroup[comboChoiceId]).map<
                ICheckComboItem
            >(
                (comboItem) =>
                    ({
                        itemId: comboItem.itemID!,
                        name: comboItem.lineDisplayName,
                        quantity: comboItem.orderQuantity,
                        variantOptions:
                            comboItem?.orderLineComboVariantOptions?.map<
                                IVariantOption
                            >((i) => ({
                                variantOptionId: i?.variantOptionID!,
                                name: i?.variantOption?.name!,
                                priceAdd: i.priceAdd,
                            })) ?? [],
                        modifierOptions:
                            comboItem.orderLineModifiers?.map<IModifierOption>(
                                (i) => ({
                                    modifierOptionId: i.modifierOptionID!,
                                    name:
                                        i.modifierOption?.displayName ||
                                        i.modifierOption?.itemName ||
                                        'Unknown',
                                    priceAdd: i?.priceAdd,
                                    bindingModfierGroupId: i.bindingModifierGroupID!,
                                }),
                            ) ?? [],
                    } as ICheckComboItem),
            )
            this.validateComboChoices(
                orderLine.itemID!,
                orderLine?.displayName ?? 'Unknown',
                comboItems,
                comboChoiceId,
                sum(comboItems.map((i) => i.quantity)),
                orderLine.menuID!,
            )
        })

        this.filterDuplicateError()
    }

    /**validate item & variant and modfier and combo to see unavailable, out of stock. we couldn't validate item price because it has been transformed to view model.
     * @param orderItem the transformed order item view in-used eg.. cart item
     */
    validateOrderItemsView(orderItem: OrderItemViewModel) {
        const item = orderItem.item
        // validate item
        if (values(orderItem.usedVariants).length < 1) {
            this.validateItem(
                item.itemId,
                item.baseItemId || item.baseDepositAndCreditItemId,
                item.displayName || item?.name || 'Unknown',
                item.categoryId,
                orderItem.menuId!,
                orderItem.menuSectionId,
                undefined,
                this.itemsMap[item.itemId!]?.type === ItemType.Miscellaneous,
            )
        }

        // validate variant
        var variantItem = item.variations.find((i) =>
            isEqual(
                i.combinationIds.sort(),
                values(orderItem.usedVariants).sort(),
            ),
        )
        if (variantItem) {
            this.validateItem(
                variantItem.itemId!,
                variantItem.baseItemId,
                variantItem.displayName || variantItem?.name || 'Unknown',
                variantItem.categoryId,
                orderItem.menuId!,
                orderItem.menuSectionId,
                undefined,
                this.itemsMap[variantItem?.baseItemId!]?.type ===
                    ItemType.Miscellaneous,
            )
        }

        // validate modifier
        for (const bindingId in orderItem.usedModifiers) {
            orderItem.usedModifiers[bindingId]
                .flat()
                .forEach((usedModifiers) => {
                    for (const key in usedModifiers.subModifierOptions) {
                        usedModifiers.subModifierOptions[key].forEach(
                            (subMod) => {
                                this.validateModifier(
                                    bindingId,
                                    subMod.modifierOptionId,
                                    this.modifierOptionsMap[
                                        subMod.modifierOptionId
                                    ]?.displayName || 'Unknown',
                                    undefined,
                                    orderItem.menuId!,
                                )
                            },
                        )
                    }
                })
        }

        // validate combo
        if (orderItem.usedItems) {
            let comboChoiceGroup = groupBy(
                values(orderItem.usedItems),
                (i) => i.comboChoiceId,
            )
            let refComboChoice = this.comboChoicesMap[orderItem.item.itemId]
            keys(comboChoiceGroup).forEach((comboChoiceId) => {
                let comboItems =
                    comboChoiceGroup[comboChoiceId]?.map((comboItem) =>
                        this.buildComboItemFromView(
                            comboItem,
                            refComboChoice,
                            comboChoiceId,
                        ),
                    ) ?? []

                this.validateComboChoices(
                    orderItem.item.itemId!,
                    orderItem.item.displayName ||
                        orderItem.item.name ||
                        'Unknown',
                    comboItems,
                    comboChoiceId,
                    undefined,
                    orderItem.menuId!,
                )
            })
        }

        this.filterDuplicateError()
        orderItem.error = this.errors
        return orderItem
    }

    /**
     *
     * @param usedItem
     * @param refComboChoice
     * @param comboChoiceId
     * @returns ICheckComboItem
     */
    buildComboItemFromView(
        usedItem: UsedItem,
        refComboChoice: ComboViewModel[],
        comboChoiceId: string,
    ) {
        const comboChoice = refComboChoice?.find(
            (i) => i.comboChoiceID === comboChoiceId,
        )

        return {
            itemId: usedItem.nodeItemId,
            name: comboChoice?.name ?? 'Unknown',
            quantity: undefined,
            variantOptions: this.getVariantOptionsView(usedItem.usedVariants),
            modifierOptions: this.getModifierOptionsView(
                usedItem.usedModifiers,
            ),
        } as ICheckComboItem
    }

    private getVariantOptionsView(usedVariants: UsedVariants) {
        return (
            keys(usedVariants)?.map((vkey) => ({
                variantOptionId: usedVariants[vkey],
                name:
                    this.variantGroupsMap[vkey]?.variantOptions?.find(
                        (i) => i.variantOptionID === usedVariants[vkey],
                    )?.name || 'Unknown',
                priceAdd: undefined,
            })) ?? []
        )
    }

    private getModifierOptionsView(usedModifiers: UsedModifiers) {
        return values(usedModifiers)
            .flatMap((i) =>
                i.flatMap((c) =>
                    values(c.subModifierOptions)
                        .flatMap((s) => s.map((a) => a.modifierOptionId))
                        .concat(c.modifierOptionId),
                ),
            )
            .map(
                (modifierOptionId) =>
                    ({
                        modifierOptionId: modifierOptionId,
                        name:
                            this.modifierOptionsMap[modifierOptionId]
                                .displayName || 'Unknown',
                        priceAdd: undefined,
                        bindingModfierGroupId: '',
                    } as IModifierOption),
            )
    }

    /**
     * validate (1)combo choices - where by item id in system combo, (2)variant option eachs combo choice items, (3)modifier option eachs combo choice items.
     * @param comboId parent item of combo
     * @param comboName name of combo
     * @param comboItems reform combo items from order
     * @param comboChoiceId combo choice id
     * @param orderedQuantity saved quantity of combo
     * @param menuId the menus that used in pos
     */
    validateComboChoices(
        comboId: string,
        comboName: string,
        comboItems: ICheckComboItem[],
        comboChoiceId: string,
        orderedQuantity: number | undefined,
        menuId: string,
    ) {
        // get real data from combo to validate
        let { refComboChoice, refComboChoiceOptions } = this.getRefComboChoice(
            comboId,
            comboChoiceId,
        )

        if (!refComboChoice) {
            this.errors['item-unavailable'].push({
                message: `${comboName} item was changed`,
            })
            return
        }

        // check combo choices items, variant option , modifier option
        comboItems.forEach((comboItem) => {
            // validate combo choice item
            if (
                this.unavailableProducts.unavailableSalesItems?.some(
                    (i) => i.itemID === comboItem.itemId,
                )
            ) {
                this.errors['item-unavailable'].push({
                    message: `${
                        refComboChoice?.name ?? 'Unknown'
                    } is unavailable.`,
                })
            }
            if (
                this.unavailableProducts.unavailableStockItems?.some(
                    (i) => i.itemID === comboItem.itemId,
                )
            ) {
                this.errors['item-unavailable'].push({
                    message: `${
                        refComboChoice?.name ?? 'Unknown'
                    } is out of stock.`,
                })
            }

            // validate combo variant options
            if (comboItem.variantOptions.length > 0) {
                comboItem.variantOptions.forEach((comboOption) => {
                    const refChoiceOption = refComboChoiceOptions.find(
                        (ref) =>
                            ref.variantOptionID === comboOption.variantOptionId,
                    )

                    if (!refChoiceOption) {
                        this.errors['item-unavailable'].push({
                            message: `${refComboChoice?.name ?? 'Unknown'} ${
                                comboOption.name
                            } is unavailable.`,
                        })
                    } else if (
                        refChoiceOption.addPrice !== comboOption.priceAdd
                    ) {
                        // ignore validate price
                        if (!comboOption.priceAdd) return

                        this.errors['price-changed'].push({
                            message: `${refComboChoice?.name ?? 'Unknown'} ${
                                comboOption.name
                            } price changed ${comboOption.priceAdd} -> ${
                                refChoiceOption.addPrice
                            }`,
                        })
                    }
                })
            }

            // validate combo modifier options
            if (comboItem.modifierOptions.length > 0) {
                comboItem.modifierOptions.forEach((modOption) => {
                    this.validateModifier(
                        modOption.bindingModfierGroupId,
                        modOption.modifierOptionId,
                        modOption.name || 'Unknown',
                        modOption.priceAdd,
                        menuId,
                    )
                })
            }
        })

        // ignore validate quantity
        if (orderedQuantity === undefined) return

        if (orderedQuantity !== refComboChoice.quantity) {
            this.errors['item-unavailable'].push({
                message: `${
                    refComboChoice?.name ?? 'Unknown'
                } has change quantity ${orderedQuantity} -> ${
                    refComboChoice.quantity
                }`,
            })
        }
    }

    private getRefComboChoice(comboId: string, comboChoiceId: string) {
        let refComboChoice = this.comboChoicesMap[comboId].find(
            (i) => i.comboChoiceID === comboChoiceId,
        )
        let refComboChoiceOptions =
            refComboChoice?.comboItems
                ?.flatMap((i) => i.variantGroups)
                .flatMap((i) => i.variantOptions)
                .map((i) => ({
                    variantOptionID: i.variantOptionID,
                    addPrice: i.addPrice,
                })) ?? []
        return {
            refComboChoice,
            refComboChoiceOptions,
        }
    }
    private validateItem(
        itemId: string,
        baseItemId: string | null | undefined,
        itemName: string,
        categoryID: string,
        menuId: string,
        menuSectionId: string,
        orderedPrice: number | undefined,
        isOpenItemId: boolean,
    ) {
        if (!this.itemsMap[baseItemId || itemId]) {
            this.errors['item-unavailable'].push({
                message: `${itemName} is in-active or removed.`,
            })
            return
        }
        if (
            this.unavailableProducts.unavailableSalesItems?.some(
                (i) => i.itemID === itemId,
            )
        ) {
            this.errors['item-unavailable'].push({
                message: `${itemName} is unavailable.`,
            })
        }

        if (
            this.strictTimeAvailability &&
            !checkItemTimeAvailabilityInMenu(
                itemId,
                [],
                categoryID,
                menuSectionId,
                this.menus.find((a) => a.menuId === menuId),
            )
        ) {
            this.errors['time-unavailable'].push({
                message: `${itemName} is unavailable at this time.`,
            })
        }

        if (
            this.enableInventoryCheck &&
            this.unavailableProducts.unavailableStockItems?.some(
                (i) => i.itemID === itemId,
            )
        ) {
            this.errors['item-unavailable']?.push({
                message: `${itemName} is out of stock`,
            })
        }

        if (orderedPrice === undefined) return // ignore check price changed

        const currentPrice = isOpenItemId
            ? orderedPrice
            : getItemPrice(itemId, menuId, this.priceListData)
        if (currentPrice !== orderedPrice) {
            this.errors['price-changed'].push({
                message: `${itemName} price changed ${orderedPrice} -> ${currentPrice}`,
            })
        }
    }
    private validateModifier(
        bindingModifierId: string, // possible is itemModifierID, itemCategoryModifierID, modifierOptionModifierID
        modifierOptionId: string,
        modifierOptionName: string,
        modifierPriceAdd: number | undefined,
        menuId: string,
    ) {
        if (this.modifierOptionsMap[modifierOptionId]) {
            const { modifierGroupID } = this.modifierOptionsMap[
                modifierOptionId
            ]
            const refOption = this.modifierOptionsMap[modifierOptionId]

            if (!refOption) {
                this.errors['item-unavailable'].push({
                    message: `${
                        modifierOptionName || 'Unknown'
                    } was remove or changed.`,
                })
                return
            }
            if (
                !refOption.locationModifierOptions?.[0]?.isAvailableForSelection
            ) {
                this.errors['item-unavailable'].push({
                    message: `${refOption.displayName} is unavailable.`,
                })
            }
            if (
                this.enableInventoryCheck &&
                this.unavailableProducts.unavailableStockItems?.some(
                    (i) => i.itemID === (refOption.itemID ?? ''),
                )
            ) {
                this.errors['item-unavailable']?.push({
                    message: `${getModOptionName(
                        refOption,
                    )} option is out of stock.`,
                })
            }

            const refBindingModifier = this.entityModifiersMap[
                bindingModifierId
            ]
            if (!refBindingModifier) return

            if (!refBindingModifier.isActive)
                this.errors['item-unavailable'].push({
                    message: `${
                        this.modifierGroupsMap[modifierGroupID!].name
                    } is unavailable.`,
                })

            if (modifierPriceAdd === undefined) return

            const cPrice = refBindingModifier.hasAssociatedCost
                ? getModifierOptionPrice(
                      modifierOptionId,
                      menuId,
                      this.priceListData,
                  )
                : 0

            if (cPrice !== modifierPriceAdd)
                this.errors['price-changed'].push({
                    message: `${
                        refOption.displayName || refOption.itemName || 'Unknown'
                    } price changed ${modifierPriceAdd} -> ${cPrice}`,
                })
        } else {
            this.errors['invalid-modifier-option'].push({
                message: `${modifierOptionName} is not found.`,
            })
        }
    }
    private filterDuplicateError() {
        this.errors['item-unavailable'] = uniqWith(
            this.errors['item-unavailable'],
            isEqual,
        )
        this.errors['time-unavailable'] = uniqWith(
            this.errors['time-unavailable'],
            isEqual,
        )
        this.errors['price-changed'] = uniqWith(
            this.errors['price-changed'],
            isEqual,
        )
        this.errors['invalid-modifier-option'] = uniqWith(
            this.errors['invalid-modifier-option'],
            isEqual,
        )
    }
}
export default OrderItemViewError
